import React, { createContext, useCallback, useContext, useEffect, useMemo } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";

import {
  buildPathWithAdAccount,
  getAdAccountIdFromSearchParams,
  getHeaderSafeAdAccountId,
} from "@/lib/adAccountIdUtils";
import { setCookie } from "@/lib/cookie";

import type { OnboardingAvailabilityHoldReason } from "@/components/onboarding/OnboardingFormTypes";
import type { AdAccountRole } from "@/lib/adAccountRoles";
import type { LocaleKey } from "@/locale";

type AdAccount = {
  id: string;
  name?: string | null;
  role?: AdAccountRole | string;
};

export type AdAccountsScope = "full" | "selected_only";

export type AdAccountStatus = "active" | "inactive" | "suspended";
export type AdAccountReviewStatus = "approved" | "in_review" | "rejected";
export type AdAccountPermissions = {
  canManageAccountInfo: boolean;
  canManageAdEntities: boolean;
};
export type AccountIntegrityReviewFailureReason =
  | "failed_verification"
  | "disallowed_industry"
  | "favicon_rejected"
  | "failed_sanctions_screening"
  | "failed_persona_verification";

export type SofaUser = {
  id: string;
  email: string | null;
  name: string | null;
  accessToken: string;
  adAccounts: AdAccount[];
  adAccountsScope?: AdAccountsScope;
  seenAdsManagerBetaWelcomeModal?: boolean;
  locale?: LocaleKey | null;
  givenName?: string;
  familyName?: string;
  countryCode?: string;
};

export type GlobalContext = {
  user?: SofaUser;
  adAccountId?: string;
  adAccountContextId?: string;
  adAccountStatus?: AdAccountStatus;
  adAccountCountryCode?: string | null;
  adAccountCurrencyCode?: string | null;
  adAccountTimezone?: string | null;
  adAccountUrl?: string | null;
  adAccountIsSalesManaged?: boolean;
  adAccountAdvertiserManagementType?: string | null;
  adAccountHasAdsOnboardingIntent?: boolean;
  adAccountBrandReviewStatus?: AdAccountReviewStatus | null;
  adAccountBrandReviewReason?: string | null;
  adAccountIntegrityReviewStatus?: AdAccountReviewStatus | null;
  adAccountNeedsPersonaVerification?: boolean;
  adAccountNeedsPersonaVerificationStepUp?: boolean;
  adAccountPersonaVerificationStatus?: string | null;
  adAccountShouldBeOnHoldingScreenPostVerification?: boolean;
  adAccountAdsManagerAccessHoldReason?: "unsupported_country" | null;
  adAccountOnboardingAvailabilityHoldReason?: OnboardingAvailabilityHoldReason | null;
  adAccountIntegrityReviewFailureReason?: AccountIntegrityReviewFailureReason | null;
  adAccountPersonaVerificationApprovedBannerDismissed?: boolean;
  adAccountPermissions?: AdAccountPermissions;
  canCreateNewAdAccountWithoutLimit?: boolean;
  isCreateNewAdAccountEnabled?: boolean;
  isMobileWebAccountSetupEnabled?: boolean;
  shouldRunOnboardingCampaignSetup?: boolean;
  adsInsightsV2ReadEnabled?: boolean;
  setAdAccountId: (adAccountId: string) => void;
};

const GlobalContext = createContext<GlobalContext>({
  setAdAccountId: () => void 0,
});

export function GlobalContextProvider({
  state,
  children,
}: {
  state?: Partial<GlobalContext>;
  children: React.ReactNode;
}) {
  const [searchParams, _setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const adAccountContextId = getHeaderSafeAdAccountId(state?.adAccountId);
  const adAccountId = getAdAccountIdFromSearchParams(searchParams) ?? adAccountContextId;

  useEffect(() => {
    if (adAccountId != null) {
      setCookie("sofaLastAdAccountId", adAccountId);
    }
  }, [adAccountId]);

  const setAdAccountId = useCallback(
    (nextAdAccountId: string) => {
      const currentPath = `${location.pathname}${location.search}${location.hash}`;
      const nextPath = buildPathWithAdAccount(currentPath, nextAdAccountId);
      if (nextPath !== currentPath) {
        navigate(nextPath, { replace: adAccountId == null });
      }
    },
    [adAccountId, location.hash, location.pathname, location.search, navigate],
  );

  const value = useMemo<GlobalContext>(
    () => ({
      ...state,
      adAccountId,
      adAccountContextId,
      setAdAccountId,
    }),
    [state, adAccountId, adAccountContextId, setAdAccountId],
  );

  return <GlobalContext.Provider value={value}>{children}</GlobalContext.Provider>;
}

export function useGlobalContext() {
  // GlobalContext is not guaranteed to be defined, due to various paths in the app such as the ErrorBoundary.
  // We should gracefully handle undefined values so that those paths don't break.
  return useContext(GlobalContext);
}
